import { useSelector } from 'react-redux'

import { digObject, findArrayIndex, sortArrayBy } from '@domain-group/javascript-utils'

import { useSetState } from '@domain-group/react-hooks'

const autoselectOptions = (selectedItem, providerPriceListItemOptions, providerPriceListItemOptionValues) => {
  const optionIds = digObject(selectedItem, 'option_ids', []).sort()

  const selectedOptions = optionIds.map((id) => {
    const option = providerPriceListItemOptions[id] || {}

    const { value_ids } = option
    const firstValueId = value_ids[0]
    const firstOptionValue = providerPriceListItemOptionValues[firstValueId] || {}

    return {
      id: option.id,
      title: option.title,
      value: {
        id: firstOptionValue.id,
        price: firstOptionValue.price,
        title: firstOptionValue.title,
      },
    }
  })

  return selectedOptions
}

const handleItemSelect = (
  itemId,
  providerPriceListItems,
  providerPriceListItemOptions,
  providerPriceListItemOptionValues,
  setState,
) => {
  const selectedItem = providerPriceListItems[+itemId] || {}
  setState({
    selectedItem,
    selectedOptions: autoselectOptions(selectedItem, providerPriceListItemOptions, providerPriceListItemOptionValues),
  })
}

const handleOptionSelect = (option, optionValueId, providerPriceListItemOptionValues, state, setState) => {
  const { selectedOptions: stateSelectedOptions } = state
  const selectedOptions = [...stateSelectedOptions]

  const optionValue = providerPriceListItemOptionValues[+optionValueId] || {}

  const index = findArrayIndex(selectedOptions, option, { useObjectKey: 'id' })
  if (index !== -1) selectedOptions.splice(index, 1)

  // Only create and push the newOption if there is an optionValue
  // This allows us to blank the option by selecting Please Select...
  if (optionValue.id){
    const newOption = {
      id: option.id,
      title: option.title,
      value: {
        id: optionValue.id,
        price: optionValue.price,
        title: optionValue.title,
      },
    }
    selectedOptions.push(newOption)
  }

  setState({ selectedOptions })
}

const handlePriceListSelect = (priceList, setState) => {
  setState({
    selectedItem: {},
    selectedOptions: [],
    selectedPriceList: priceList || {},
    selectedProduct: {},
  })
}

const handleProductSelect = (productId, serviceProducts, setState) => {
  const selectedProduct = serviceProducts[+productId] || {}

  setState({
    selectedItem: {},
    selectedOptions: [],
    selectedProduct,
  })
}

const defaultState = {
  selectedItem: {},
  selectedOptions: [],
  selectedPriceList: {},
  selectedProduct: {},
}

const useProductSelector = () => {
  const [state, setState] = useSetState(defaultState)
  const {
    selectedItem,
    selectedPriceList,
    selectedProduct,
  } = state

  const {
    providerPriceListItemOptionValues,
    providerPriceListItemOptions,
    providerPriceListItems,
    providerPriceLists,
    serviceProducts,
  } = useSelector(reduxState => reduxState.entities)

  const selectedProviderPriceList = providerPriceLists[selectedPriceList?.id]
  const selectedProviderPriceListProductIds = selectedProviderPriceList?.products || []
  const availablePriceListProducts = selectedProviderPriceListProductIds
    .map(id => serviceProducts[id])
    .filter(product => product)

  const selectedProviderPriceListItemIds = selectedProviderPriceList?.items || []
  const availablePriceListItems = selectedProviderPriceListItemIds
    .map(id => providerPriceListItems[id])
    .filter(priceListItem => priceListItem?.service_product_id === selectedProduct.id)

  const selectedPriceListItem = providerPriceListItems[selectedItem.id] || {}

  const priceListOptionIds = selectedPriceListItem.option_ids || []
  const priceListOptionsArray = priceListOptionIds
    .map(id => providerPriceListItemOptions[id])
    .filter(option => option)

  const hasPriceListOptions = !!priceListOptionsArray.length

  return {
    availablePriceListItems: sortArrayBy(availablePriceListItems, 'asc', 'quantity'),
    availablePriceListProducts: sortArrayBy(availablePriceListProducts, 'asc', 'title'),
    callbacks: {
      handleItemSelect: itemId => handleItemSelect(
        itemId,
        providerPriceListItems,
        providerPriceListItemOptions,
        providerPriceListItemOptionValues,
        setState,
      ),
      handleOptionSelect: (option, optionValueId) => (
        handleOptionSelect(option, optionValueId, providerPriceListItemOptionValues, state, setState)
      ),
      handlePriceListSelect: priceList => handlePriceListSelect(priceList, setState),
      handleProductSelect: productId => handleProductSelect(productId, serviceProducts, setState),
    },
    hasPriceListOptions,
    priceListOptionsArray,
    providerPriceListItemOptionValues,
    state: { ...state, selectedPriceList: selectedProviderPriceList || selectedPriceList },
  }
}

export default useProductSelector
